import { useQuery } from '@apollo/client'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { Archive, Delete, Pencil, People, Settings } from '@factorialco/f0-react/icons/app'
import { AssetActivityLogDocument } from 'generated/resources/graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

interface ActivityLogProps {
  assetId: string
}

interface ActivityLogItem {
  id: string
  updatedAt: string
  action?: string | null
  status?: string | null
  ownerId?: number | null
  locationId?: number | null
  serialNumber?: string | null
  purchasePriceCents?: number | null
  currency?: string | null
  label?: string | null
  deletedAt?: string | null
  author?: {
    id: number
    firstName?: string | null
    lastName?: string | null
  } | null
}

const ActivityLog = ({ assetId }: ActivityLogProps) => {
  const { data, loading, error } = useQuery(AssetActivityLogDocument, {
    variables: { assetId },
    skip: !assetId,
    fetchPolicy: 'no-cache', // Disable caching to prevent object reference issues
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Spinner size="small" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-red-600">
        {i18n.t('it_management.asset_details.activity_log_error')}
      </div>
    )
  }

  const activityLogsConnection = data?.itManagement?.assetActivityLogsConnection
  const activities: ActivityLogItem[] = activityLogsConnection?.nodes || []

  if (activities.length === 0) {
    return (
      <div className="p-4 text-sm text-f1-foreground-secondary">
        {i18n.t('it_management.asset_details.no_activity')}
      </div>
    )
  }

  const getActivityDescription = (activity: ActivityLogItem) => {
    if (activity.deletedAt) {
      return i18n.t('it_management.asset_details.activity_deleted')
    }

    if (activity.action) {
      switch (activity.action) {
        case 'deleted':
          return i18n.t('it_management.asset_details.activity_deleted')
        case 'created':
          return i18n.t('it_management.asset_details.activity_created')
        case 'updated':
          return i18n.t('it_management.asset_details.activity_updated')
        case 'ownership_changed':
          return i18n.t('it_management.asset_details.activity_ownership_changed')
        case 'status_changed':
          return i18n.t('it_management.asset_details.activity_status_changed')
        default:
          return i18n.t('it_management.asset_details.activity_updated')
      }
    }

    return i18n.t('it_management.asset_details.activity_updated')
  }

  const getActivityIcon = (activity: ActivityLogItem) => {
    if (activity.deletedAt) {
      return Delete
    }

    switch (activity.status) {
      case 'in_stock':
        return Archive
      case 'assigned':
        return People
      case 'maintenance':
        return Settings
      case 'retired':
        return Archive
      default:
        return Pencil
    }
  }

  return (
    <div className="flex flex-col pl-4 pt-4 pb-4">
      {activities.map((activity, index) => {
        const IconComponent = getActivityIcon(activity)
        const isLast = index === activities.length - 1
        const isFirst = index === 0
        return (
          <div
            key={`activity-${activity.updatedAt}-${activity.action}-${activity.status}-${activity.ownerId}-${activity.locationId}-${index}`}
            className="flex min-w-0"
          >
            <div className="flex flex-col items-center mr-3 flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-f1-background-secondary flex items-center justify-center border border-f1-border-subtle flex-shrink-0">
                <IconComponent className="w-4 h-4 text-f1-foreground-secondary" />
              </div>
              {!isLast && (
                <div
                  className="w-px flex-1 pb-8"
                  style={{ backgroundColor: 'rgba(5, 35, 72, 0.14)' }}
                />
              )}
            </div>
            <div className={`flex flex-col flex-1 min-w-0 ${!isFirst ? 'pt-6' : ''}`}>
              <p className="text-sm font-medium text-f1-foreground mb-1 leading-5">
                {getActivityDescription(activity)}
                {activity.author && (
                  <>
                    {' '}
                    <span className="text-f1-foreground-secondary font-normal">
                      by{' '}
                      {`${activity.author.firstName || ''} ${
                        activity.author.lastName || ''
                      }`.trim()}
                    </span>
                  </>
                )}
              </p>
              <p className="text-xs text-f1-foreground-secondary leading-4">
                {moment(activity.updatedAt).format('Do MMMM YYYY')} ·{' '}
                {moment(activity.updatedAt).format('HH:mm')}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ActivityLog
